import { useTheme } from '@/shared/ui';

import { GX_TEXT } from '@/config/colors';
import { findClosestPoint } from '@/features/_shared/chart/lib/findClosestPoint';
import { getDayMonthOfYear } from '@/features/_shared/chart/lib/getDayMonthOfYear';
import type { SeriesContext } from '@/features/_shared/chart/types/highcharts';
import { DateTextVariants, useDateText } from '@/features/_shared/user';
import { formatPriceValue } from '@/shared/lib/format/formatPriceValue';

import type { Point } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

type SeasonalPointCustom = Partial<SeriesContext> & {
  timeRef?: string;
  timezone?: string;
  transactionDate?: number | string;
};

type SeasonalTooltipPoint = Point & {
  custom?: SeasonalPointCustom;
  x: number;
};

const parseDateString = (
  point: SeasonalTooltipPoint,
  field: string | undefined,
  formatDateString: (date: string, variant: DateTextVariants) => string
) => {
  const custom = point.custom;
  const timeRef = custom?.timeRef as string | undefined;
  const monthDate = getDayMonthOfYear({
    dayOfYear: point.x + 1,
    year: Number(field),
    hour: timeRef?.slice(0, 2),
    minute: timeRef?.slice(2),
    timeZone: custom?.timezone
  }).toJSON();
  if (field && !Number.isNaN(Number(field))) {
    const transactionDateEpoch = Number(custom?.transactionDate);
    if (!Number.isNaN(transactionDateEpoch) && transactionDateEpoch > 0) {
      const date = new Date(0);
      date.setUTCSeconds(transactionDateEpoch);
      return formatDateString(date.toISOString(), DateTextVariants.FULL);
    }
    return formatDateString(monthDate, DateTextVariants.FULL);
  }
  return formatDateString(monthDate, DateTextVariants.DAY_MONTH);
};

const createHeader = () =>
  '<div style="display: flex; flex-direction: column; width:350px;">' +
  '<div style="font-size: 8px; display: flex; font-weight: 400;">' +
  '<div style="width: 220px;">Short Title</div>' +
  '<div style="width: 65px; margin-left: 9px">Date</div><div style="width: 10%;"></div>' +
  '</div>';
const useSeasonalTooltipFormatter = () => {
  const { formatDateString } = useDateText();
  const theme = useTheme();

  const createBodyRow = (
    point: SeasonalTooltipPoint,
    seriesName: string,
    value: string,
    field?: string
  ) => {
    const nameColumn = `<div style="width: 220px; padding-right: 8px; border-right: 1px solid gray; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;">${seriesName}</div>`;
    const dateString = parseDateString(point, field, formatDateString);
    return (
      `<div style="font-size: 10px; font-style: normal;font-weight: 500;justify-content: space-between;height: 19px; display: flex;">${
        nameColumn
      }<div style="width: 65px; padding-left: 8px; padding-right: 8px;">${dateString}</div>` +
      `<div style="height 19px; padding-left:8px; padding-top:3px"><div style="background-color:${
        point.series.color ?? GX_TEXT
      }; color:${
        theme.palette.gx.mainBackground
      }; font-size:10px; line-height:13px; height:14px; width:55px; padding-left:4px; padding-right:4px; text-align: start;">${
        value
      }</div></div></div>`
    );
  };

  const createTooltipFormatter = (
    allChartSeries: Highcharts.Series[],
    hoveredPointXValue: string | number | undefined
  ) => {
    const points: Highcharts.Point[] = allChartSeries
      .filter(series => series.visible && (series as { points: Highcharts.Point[] | null }).points)
      .map(series => findClosestPoint(series.points, Number(hoveredPointXValue)))
      .filter(x => !!x);

    const header = createHeader();

    const body = points
      .flatMap(point => {
        const seasonalPoint = point as SeasonalTooltipPoint;
        const custom = seasonalPoint.custom;
        const seriesCustom = point.series.options.custom as SeriesContext;
        let value = '';
        let rows: string[] = [];
        const rangePoint = point as typeof point & { high?: number; low?: number };
        if (rangePoint.high && rangePoint.low) {
          let rangeValue = formatPriceValue(custom?.decimalPlaces ?? 2, rangePoint.high);
          rows = [...rows, createBodyRow(seasonalPoint, `${point.series.name} High`, rangeValue)];
          rangeValue = formatPriceValue(custom?.decimalPlaces ?? 2, rangePoint.low);
          rows = [...rows, createBodyRow(seasonalPoint, `${point.series.name} Low`, rangeValue)];
          return rows;
        } else if (point.y !== undefined && !Number.isNaN(point.y)) {
          value = formatPriceValue(custom?.decimalPlaces ?? 2, point.y);
        }
        return [createBodyRow(seasonalPoint, point.series.name, value, seriesCustom.field)];
      })
      .join('');
    return header + body;
  };
  return { createTooltipFormatter };
};

export default useSeasonalTooltipFormatter;
